import { useAuth } from '../contexts/AuthContext'
import { useEffect } from 'react'
import { logWarning, logInfo } from '../utils/errorLogger'

/**
 * Admin authentication hook
 *
 * Protects admin routes by checking if the current user has admin privileges.
 * Logs warnings for unauthorized access attempts.
 *
 * Usage:
 * ```tsx
 * const AdminComponent = () => {
 *   const { isAdmin, loading } = useAdminAuth()
 *
 *   if (loading) return <div>Loading...</div>
 *   if (!isAdmin) return null
 *
 *   return <div>Admin content</div>
 * }
 * ```
 */
export const useAdminAuth = () => {
  const { isAuthenticated, isAdmin, loading } = useAuth()

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        logWarning('Unauthenticated user attempted to access admin route', 'AdminAuth')
      } else if (!isAdmin) {
        logWarning('Non-admin user attempted to access admin route', 'AdminAuth')
      } else {
        logInfo('Admin user accessing admin route', 'AdminAuth')
      }
    }
  }, [isAuthenticated, isAdmin, loading])

  return { isAdmin, loading }
}

/**
 * Higher-order component for admin route protection
 * 
 * Wraps a component to ensure only admin users can access it.
 * 
 * Usage:
 * ```tsx
 * const ProtectedAdminComponent = withAdminAuth(AdminDashboard)
 * ```
 */
export const withAdminAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  const AdminAuthedComponent = (props: P) => {
    const { isAdmin, loading } = useAdminAuth()

    if (loading) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          <div>Loading...</div>
        </div>
      )
    }

    if (!isAdmin) {
      return null
    }

    return <Component {...props} />
  }
  AdminAuthedComponent.displayName = `withAdminAuth(${Component.displayName || Component.name || 'Component'})`
  return AdminAuthedComponent
}

